import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { IconEdit, List, TextInput, Notice, Selector } from 'shared/components';
import { formatCustomFieldsToTable } from './helpers';
import './styles.scss';
import { useTranslation } from 'react-i18next';

const CustomFieldsEditableTable = ({
  selectedCustomFields,
  setErrorState,
  handleFieldsEdit,
  productCustomFields = [],
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [hasErrors, setHasErrors] = useState(false);
  const [cFields, setCFields] = useState(
    formatCustomFieldsToTable(selectedCustomFields)
  );
  const [selectedCustomFieldEnums, setSelectedCustomFieldEnums] = useState([]);

  useEffect(() => {
    findTargetCustomFieldEnums();
    const newCFields = formatCustomFieldsToTable(selectedCustomFields);
    const errors = newCFields.some((cf) => cf.cf_value_error);
    setCFields(newCFields);
    setErrorState(errors);
    setHasErrors(errors);
  }, [selectedCustomFields, JSON.stringify(productCustomFields)]);

  const submitChange = (newCFields) => {
    setCFields(newCFields);
    const errors = newCFields.some((cf) => cf.cf_value_error);
    if (errors) {
      setHasErrors(true);
      setErrorState(true);
      return false;
    }
    setHasErrors(false);
    setErrorState(false);
    handleFieldsEdit(newCFields);
    return true;
  };

  const findTargetCustomFieldEnums = () => {
    if (!productCustomFields || !productCustomFields.length) return;
    if (!selectedCustomFields || !selectedCustomFields.length) return;

    const targetEnums = selectedCustomFields.reduce((acc, { value }) => {
      if (!value || !value) return acc;

      const match = productCustomFields.find((pcf) => pcf && pcf.id === value);

      if (match) {
        acc.push({
          customFieldID: value,
          product_custom_field_enums:
            match.product_custom_field_enums &&
            Array.isArray(match.product_custom_field_enums)
              ? match.product_custom_field_enums
              : [],
        });
      }

      return acc;
    }, []);
    setSelectedCustomFieldEnums(targetEnums);
  };

  const handleValueChange = (cellData, val) => {
    const fieldID = get(cellData, 'original.value');
    const updatedFields = cFields.map((cf) => {
      if (cf.value === fieldID) {
        const isError = val === null || val === undefined ? 'error' : '';
        const newCF = {
          ...cf,
          cf_value: val,
          cf_value_error: isError,
        };
        return newCF;
      }
      return cf;
    });
    submitChange(updatedFields);
  };

  const renderValueEditable = (cellData) => {
    const field = cFields.find((cf) => cf.value === cellData.original.value);
    const cellValue = get(field, 'cf_value', '');
    const fieldValue = get(field, 'value');
    const fieldEnums = get(field, 'data.product_custom_field_enums', []);
    const defaultVal = get(field, 'data.default_value');

    const selectedEnum = selectedCustomFieldEnums.find(
      (scfe) => scfe.customFieldID === fieldValue
    );

    const enums =
      selectedEnum && selectedEnum.product_custom_field_enums
        ? selectedEnum.product_custom_field_enums
        : fieldEnums;

    const hasEnums = Array.isArray(enums) && enums.length > 0;

    if (isLoading) {
      return (
        <Selector
          value={''}
          disabled={true}
          options={[]}
          placeholder={t('Loading...')}
        />
      );
    }

    if (hasEnums) {
      const options = enums.map((enumValue) => ({
        label: get(enumValue, 'value'),
        value: get(enumValue, 'value'),
      }));

      if (defaultVal !== undefined && defaultVal !== null) {
        const hasDefaultOption = options.some(
          (option) => option.value === defaultVal
        );
        if (!hasDefaultOption) {
          options.unshift({
            label: defaultVal === '' ? t('(Empty)') : defaultVal,
            value: defaultVal,
          });
        }
      }

      if (
        cellValue !== undefined &&
        cellValue !== null &&
        !options.some((option) => option.value === cellValue)
      ) {
        options.unshift({
          label: cellValue === '' ? t('(Empty)') : cellValue,
          value: cellValue,
        });
      }

      return (
        <Selector
          value={cellValue}
          options={options}
          handleChange={(val) => handleValueChange(cellData, val)}
          placeholder={t('Select value')}
        />
      );
    }

    return (
      <TextInput
        defaultValue={cellValue}
        error={cellData.original.cf_value_error}
        disabled={hasErrors && !cellData.original.cf_value_error}
        handleChange={() => {}}
        onBlur={(e) => handleValueChange(cellData, e.target.value)}
      />
    );
  };

  return (
    <div className="CustomFieldsEditableTable">
      {hasErrors && (
        <Notice size="sm" theme="error">
          {t('Fix erorrs to continue saving the edits')}
        </Notice>
      )}
      <List
        columns={[
          { accessor: 'name', Header: t('Name') },
          {
            accessor: 'cf_value',
            Header: () => (
              <div className="custom-header">
                {t('Value')}
                <IconEdit height="12px" width="12px" fill="#909090" />
              </div>
            ),
            headerClassName: 'editable-column',
            Cell: (cellData) => renderValueEditable(cellData),
          },
        ]}
        data={cFields}
        clickable={false}
        showPagination={false}
        minRows={2}
        resizable={false}
      />
    </div>
  );
};

CustomFieldsEditableTable.propTypes = {
  selectedCustomFields: PropTypes.array,
  setErrorState: PropTypes.func.isRequired,
  handleFieldsEdit: PropTypes.func.isRequired,
  productCustomFields: PropTypes.arrayOf(PropTypes.object),
};

CustomFieldsEditableTable.defaultProps = {
  selectedCustomFields: [],
};

export default CustomFieldsEditableTable;
